import React, { useState } from 'react';
import { Collapse, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import AdvancedTargetingModal from './AdvancedTargetingModal';
import {
  AdvancedTargetingWrapper,
  AdvancedTargetingCollapse,
  AdvancedTargetingHeader,
  AdvancedTargetingGrid,
  AddDefinitionBox,
  DefinitionCard,
} from './CreateCampaignStyle';

const { Panel } = Collapse;

const AdvancedTargetingSection = ({
  advancedTargeting = [],
  onChange,
  disabled = false,
  // Quota props
  quotas = [],
  onOpenQuotaModal,
  onDeleteQuota,
  responsesValue,
  isEditDisabled = false,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAddClick = () => {
    if (disabled) return;
    setEditingIndex(null);
    setModalVisible(true);
  };

  const handleEditClick = (index) => {
    if (disabled) return;
    setEditingIndex(index);
    setModalVisible(true);
  };

  const handleRemove = (index) => {
    if (disabled) return;
    const item = advancedTargeting[index];
    // Clear quota for this AT question when removing
    if (item && onDeleteQuota) {
      onDeleteQuota(`at_${item.qualification_code}`);
    }
    const newItems = [...advancedTargeting];
    newItems.splice(index, 1);
    onChange(newItems);
  };

  const handleModalSave = (item) => {
    const newItems = [...advancedTargeting];

    if (editingIndex !== null) {
      const oldItem = advancedTargeting[editingIndex];
      // Clear quota if options changed (condition_codes differ)
      if (oldItem && onDeleteQuota) {
        const oldCodes = (oldItem.condition_codes || []).sort().join(',');
        const newCodes = (item.condition_codes || []).sort().join(',');
        if (oldCodes !== newCodes) {
          onDeleteQuota(`at_${oldItem.qualification_code}`);
        }
      }
      // Editing existing item
      newItems[editingIndex] = {
        ...item,
        id: advancedTargeting[editingIndex].id,
      };
    } else {
      // Adding new item
      newItems.push({
        ...item,
        id: `at_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      });
    }

    onChange(newItems);
    setModalVisible(false);
    setEditingIndex(null);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingIndex(null);
  };

  const getQuotaForAtField = (qualificationCode) => {
    return quotas.find(q => q.field === `at_${qualificationCode}`);
  };

  const getAtQuotaTooltip = (qualificationCode) => {
    const quota = getQuotaForAtField(qualificationCode);
    if (!quota || !quota.entries?.length) return null;
    const total = quota.entries.length;
    const totalCount = quota.entries.reduce((s, e) => s + (e.count || 0), 0);
    const scrollStyle = {
      maxHeight: '240px', overflowY: 'auto', marginRight: '-6px', paddingRight: '10px',
      scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.25) transparent',
    };
    return (
      <div style={{ padding: '4px 2px', fontSize: '12px', lineHeight: '1.7' }}>
        <div style={total > 10 ? scrollStyle : undefined}>
          {quota.entries.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>{e.label || '—'}</span>
              <span style={{ fontWeight: 600 }}>{e.count}</span>
            </div>
          ))}
        </div>
        {total > 10 && (
          <>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>{total} entries total</span>
              <span style={{ fontWeight: 600 }}>{totalCount}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderSelectionTags = (item) => {
    const labels = item.selected_labels || [];
    const maxVisible = 3;
    const visibleLabels = labels.slice(0, maxVisible);
    const remaining = labels.length - maxVisible;

    return (
      <div className="definition-selections">
        {visibleLabels.map((label, idx) => (
          <span key={idx} className="selection-tag">
            {label}
          </span>
        ))}
        {remaining > 0 && (
          <span className="selection-more">+{remaining} more</span>
        )}
      </div>
    );
  };

  const renderQuotaAction = (item) => {
    if (!onOpenQuotaModal) return null;

    const quota = getQuotaForAtField(item.qualification_code);
    const hasQuota = quota && quota.entries?.length > 0;
    const canAdd = responsesValue && responsesValue > 0 && (item.condition_codes || []).length > 0;
    const atField = `at_${item.qualification_code}`;

    if (hasQuota) {
      return (
        <div className="definition-quota">
          <span className="at-quota-badge">
            <Tooltip title={getAtQuotaTooltip(item.qualification_code)} placement="bottom">
              <InfoCircleOutlined className="at-quota-icon" style={{ color: '#3d2969', cursor: 'pointer' }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
            <span>Quota</span>
            <EditOutlined
              className="at-quota-icon edit-icon"
              onClick={(e) => { e.stopPropagation(); onOpenQuotaModal(atField, item.qualification_name); }}
            />
            <Popconfirm
              title="Remove quota?"
              description="This will remove the quota for this question."
              onConfirm={() => onDeleteQuota(atField)}
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <DeleteOutlined
                className="at-quota-icon delete-icon"
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </span>
        </div>
      );
    }

    const isDisabled = isEditDisabled || !canAdd;
    const tooltipMsg = !responsesValue || responsesValue <= 0
      ? 'Add responses first'
      : 'Select options first';

    const btn = (
      <button
        type="button"
        className="at-add-quota-btn"
        onClick={(e) => { e.stopPropagation(); onOpenQuotaModal(atField, item.qualification_name); }}
        disabled={isDisabled}
      >
        <PlusOutlined style={{ fontSize: '10px' }} /> Quota
      </button>
    );

    return (
      <div className="definition-quota">
        {isDisabled ? (
          <Tooltip title={tooltipMsg} placement="top">
            <span style={{ display: 'inline-flex' }}>{btn}</span>
          </Tooltip>
        ) : btn}
      </div>
    );
  };

  const collapseHeader = (
    <AdvancedTargetingHeader>
      <span className="header-title">Advanced Targeting</span>
      {advancedTargeting.length > 0 && (
        <span className="header-count">{advancedTargeting.length}</span>
      )}
      <span className="header-subtitle">
        {advancedTargeting.length === 0 ? 'Click to add targeting criteria' : ''}
      </span>
    </AdvancedTargetingHeader>
  );

  return (
    <AdvancedTargetingWrapper>
      <AdvancedTargetingCollapse
        expandIcon={({ isActive }) => (
          <RightOutlined
            rotate={isActive ? 90 : 0}
            style={{ color: '#3d2969', fontSize: 12, transition: 'transform 0.2s' }}
          />
        )}
        defaultActiveKey={advancedTargeting.length > 0 ? ['1'] : []}
      >
        <Panel header={collapseHeader} key="1">
          <AdvancedTargetingGrid>
            {/* Existing definitions */}
            {advancedTargeting.map((item, index) => (
              <DefinitionCard key={item.id || index}>
                <div className="definition-header">
                  <span className="definition-title">
                    {item.qualification_name || 'Untitled'}
                  </span>
                  {!disabled && (
                    <div className="definition-actions">
                      <EditOutlined
                        className="action-icon edit-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(index);
                        }}
                      />
                      <Popconfirm
                        title="Remove this definition?"
                        description="This action cannot be undone."
                        onConfirm={() => handleRemove(index)}
                        okText="Remove"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <DeleteOutlined
                          className="action-icon delete-icon"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    </div>
                  )}
                </div>
                {renderSelectionTags(item)}
                {renderQuotaAction(item)}
              </DefinitionCard>
            ))}

            {/* Add Definition box */}
            {!disabled && (
              <AddDefinitionBox onClick={handleAddClick}>
                <span className="add-text">
                  <PlusOutlined style={{ marginRight: 6 }} />
                  Add Definition
                </span>
              </AddDefinitionBox>
            )}
          </AdvancedTargetingGrid>
        </Panel>
      </AdvancedTargetingCollapse>

      <AdvancedTargetingModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSave={handleModalSave}
        editingItem={editingIndex !== null ? advancedTargeting[editingIndex] : null}
        existingQuestionCodes={advancedTargeting.map((item) => item.qualification_code)}
      />
    </AdvancedTargetingWrapper>
  );
};

export default AdvancedTargetingSection;
